import './SignatureKey.scss';

import React, { useEffect, useRef, useState } from 'react';
import { useSplitInput } from 'componentsV2/SplitInput/useSplitInput';
import FullForm from '../../ares/Layouts/FullForm';
import { SplitInput } from 'componentsV2/SplitInput/SplitInput';
import { ValidationsList } from './ValidationsList';
import {
    createAscendingOrDescendingValidation,
    createBirthdayValidation,
    createRepeatedValidation,
} from 'componentsV2/SplitInput/validations';
import { connect } from 'react-redux';
import { convertToValidFormatForDate, getDay, getFullYear, getMonth } from 'utilities/dateHelper/dateHelper';
import { formatText } from 'core/i18n';
import { bindActionCreators } from 'redux';
import modalCancelActions from 'actions/modalCancelActions';
import { SIGNATURE_KEY_ENABLED } from 'constants/featureFlagging';
import PropTypes from 'prop-types';
import withFeatureToggling from '../../hocs/withFeatureToggling/withFeatureToggling';
import { saveSignatureKey } from 'actions/signatureKeyActions';
import { welcomeCard as WelcomeCard } from 'components/welcomeCard/welcomeCard';
import { useModal } from 'hooks/useModal';
import { MoreInformationModal } from './MoreInformationModal';
import { ONLY_NUMERIC_REGEX } from 'constants/regex';
import { useOutsideAlerter } from 'hooks/useOutsideAlerter';
import MessageBox from 'components/commons/MessageBox/MessageBox';
import {
    doTagCancelAction,
    doTagContinueAction,
    doTagInputAction,
    doTagMoreInfoAction,
    doTagPage,
    doTagSaveSignatureKeySuccess,
    doTagTogglePasswordAction,
} from './tagging';
import { setPageName } from 'actions/errorViewNavigation';

const SIGNATURE_KEY_LENGTH = 8;
const BASE_VALIDATIONS = [createRepeatedValidation(), createAscendingOrDescendingValidation(SIGNATURE_KEY_LENGTH)];

const SignatureKey = ({ userProfile, modalCancelActions, flags, saveSignatureKey, moveToNextStep, setPageName }) => {
    const firstFocus = useRef(true);
    const [showIncompleteKeyAdvise, setShowIncompleteKeyAdvise] = useState(false);
    const splitInputContainerRef = useRef();

    const birthDate = userProfile.birthDate && new Date(convertToValidFormatForDate(userProfile.birthDate));
    const { birthDay, birthMonth, birthYear } = userProfile;

    const validations = birthDate
        ? [
              ...BASE_VALIDATIONS,
              createBirthdayValidation({
                  day: birthDay || String(getDay(birthDate)),
                  month: birthMonth || String(getMonth(birthDate)),
                  year: birthYear || String(getFullYear(birthDate)),
              }),
          ]
        : BASE_VALIDATIONS;

    const { isOpen, toggleModal } = useModal();
    const {
        value: signatureKeyValue,
        hasError,
        isCompleted,
        showCharacters,
        notPassedValidations,
        onChange,
        toggleShowCharacters,
    } = useSplitInput({ validations, defaultShowCharacters: false, howManyCharacters: SIGNATURE_KEY_LENGTH });

    const handleOnChange = props => {
        onChange(props);
    };

    const handleClickTogglePassword = event => {
        event.preventDefault();
        toggleShowCharacters();

        doTagTogglePasswordAction();
    };

    const handleContinueAction = () => {
        doTagContinueAction();

        const saveSignatureCallback = () => {
            doTagSaveSignatureKeySuccess({ investmentType: userProfile.investmentType });
            moveToNextStep();
        };

        saveSignatureKey({
            signatureKey: signatureKeyValue,
            successCallback: saveSignatureCallback,
        });
    };

    const handleCancelAction = () => {
        modalCancelActions.showModalCancel();
        doTagCancelAction();
    };

    const handleFocusOut = () => {
        !isCompleted && signatureKeyValue.length > 1 && setShowIncompleteKeyAdvise(true);
    };

    const handleFocusIn = () => {
        if (firstFocus.current) {
            doTagInputAction();
            firstFocus.current = false;
        }
        setShowIncompleteKeyAdvise(false);
    };

    const handleValidateField = value => ONLY_NUMERIC_REGEX.test(value);

    const handleClickMoreInformation = () => {
        doTagMoreInfoAction();

        toggleModal();
    };

    useOutsideAlerter(splitInputContainerRef, () => handleFocusOut());

    useEffect(() => {
        setPageName(doTagPage({ userProfile }));
    }, []);

    if (!flags.get(SIGNATURE_KEY_ENABLED)) {
        moveToNextStep();

        return null;
    }

    return (
        <>
            <MoreInformationModal isOpen={isOpen} closeAction={toggleModal} />
            <WelcomeCard />
            <FullForm
                icon="firma"
                onSubmit={() => {}}
                formModel="userProfile"
                hideRoadMapNavigation={false}
                cancelAction={handleCancelAction}
                continueAction={handleContinueAction}
                title={formatText('stepsTitles-signatureKey')}
                continueButtonEnabled={isCompleted && !hasError}
            >
                <div className="signature-key">
                    <div>
                        <p className="signature-key__info">{formatText('signatureKey-title')}</p>
                        <p className="signature-key__more-info" onClick={handleClickMoreInformation}>
                            {formatText('signatureKey-moreinfo')}
                        </p>
                    </div>
                    <div
                        className="signature-key__split-input-container"
                        ref={splitInputContainerRef}
                        data-testid="singature-key-container"
                    >
                        <SplitInput
                            onChange={handleOnChange}
                            howManyCharacters={SIGNATURE_KEY_LENGTH}
                            value={signatureKeyValue}
                            showCharacters={showCharacters}
                            type="tel"
                            noPaste
                            className="signature-key__input"
                            label={formatText('signatureKey-label')}
                            hasError={hasError || showIncompleteKeyAdvise}
                            autoComplete="off"
                            validateField={handleValidateField}
                            isCompleted={isCompleted}
                            onFocusIn={handleFocusIn}
                        />
                        <a href="#" className={'signature-key__show-characters'} onClick={handleClickTogglePassword}>
                            {formatText(`signatureKey-${showCharacters ? 'hideKey' : 'showKey'}`)}
                        </a>
                    </div>
                    <div className="signature-key__validation-list">
                        <ValidationsList
                            validationsList={validations}
                            notPassedValidations={notPassedValidations}
                            valueCompleted={isCompleted}
                        />
                    </div>
                    {showIncompleteKeyAdvise && (
                        <MessageBox
                            boxColor="red"
                            iconColor="alert-red"
                            contentText={formatText('signatureKey-warningMessage')}
                        />
                    )}
                </div>
            </FullForm>
        </>
    );
};

SignatureKey.propTypes = {
    userProfile: PropTypes.object.isRequired,
    immStepsState: PropTypes.object.isRequired,
    modalCancelActions: PropTypes.object.isRequired,
    flags: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
    saveSignatureKey: PropTypes.func.isRequired,
    moveToNextStep: PropTypes.func.isRequired,
};

export default connect(
    state => ({
        userProfile: state.userProfile,
    }),
    dispatch => ({
        dispatch,
        modalCancelActions: bindActionCreators(modalCancelActions, dispatch),
        saveSignatureKey: bindActionCreators(saveSignatureKey, dispatch),
        setPageName: bindActionCreators(setPageName, dispatch),
    })
)(withFeatureToggling(SignatureKey));
